import React, { useState } from 'react';
import { Key, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants';
import { validateLicenseKey, activateLicenseKey, storeLicense } from '../lib/license';

export function LicenseKeyInput({ onActivated, compact = false }) {
  const [licenseKey, setLicenseKey] = useState('');
  const [status, setStatus] = useState('idle'); // idle | validating | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const key = licenseKey.trim();
    if (!key) return;

    setStatus('validating');
    setErrorMessage('');

    try {
      // First validate
      const validation = await validateLicenseKey(key);

      if (!validation.valid) {
        setStatus('error');
        setErrorMessage(validation.error || 'Invalid license key');
        return;
      }

      // Then activate (counts against max activations)
      const activation = await activateLicenseKey(key);

      if (!activation.activated) {
        setStatus('error');
        setErrorMessage(activation.error || 'Activation failed');
        return;
      }

      // Store locally
      storeLicense({
        licenseKey: key,
        activationId: activation.activationId,
      });

      setStatus('success');

      // Notify parent after brief delay so user sees the success state
      setTimeout(() => {
        if (onActivated) onActivated();
      }, 800);
    } catch (err) {
      setStatus('error');
      setErrorMessage('Could not connect to license server. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: compact ? '8px 12px' : '16px',
        borderRadius: '12px',
        backgroundColor: EARTH.sage[50],
        border: `1px solid ${EARTH.sage[200]}`,
        fontFamily: TYPOGRAPHY.body,
      }}>
        <CheckCircle style={{ width: '20px', height: '20px', color: EARTH.sage[600], flexShrink: 0 }} />
        <span style={{ color: EARTH.sage[700], fontWeight: 600, fontSize: '14px' }}>
          License activated — welcome!
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ fontFamily: TYPOGRAPHY.body }}>
      <div style={{
        display: 'flex', gap: '8px',
        marginBottom: errorMessage ? '8px' : '0',
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Key style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            width: '16px', height: '16px', color: EARTH.stone[400],
          }} />
          <input
            type="text"
            value={licenseKey}
            onChange={(e) => { setLicenseKey(e.target.value); setStatus('idle'); setErrorMessage(''); }}
            placeholder="Enter your license key"
            disabled={status === 'validating'}
            style={{
              width: '100%',
              padding: compact ? '8px 12px 8px 36px' : '12px 16px 12px 40px',
              borderRadius: '10px',
              fontSize: compact ? '13px' : '15px',
              border: `1px solid ${status === 'error' ? EARTH.terracotta[300] : EARTH.stone[300]}`,
              backgroundColor: '#fff',
              outline: 'none',
              fontFamily: TYPOGRAPHY.body,
              color: EARTH.ink || EARTH.stone[800],
              boxSizing: 'border-box',
            }}
          />
        </div>
        <button
          type="submit"
          disabled={status === 'validating' || !licenseKey.trim()}
          style={{
            padding: compact ? '8px 16px' : '12px 20px',
            borderRadius: '10px',
            fontSize: compact ? '13px' : '15px',
            fontWeight: 600,
            backgroundColor: EARTH.terracotta[500],
            color: '#fff',
            border: 'none',
            cursor: status === 'validating' ? 'wait' : 'pointer',
            fontFamily: TYPOGRAPHY.body,
            opacity: (status === 'validating' || !licenseKey.trim()) ? 0.6 : 1,
            transition: 'opacity 0.2s',
            display: 'flex', alignItems: 'center', gap: '6px',
            whiteSpace: 'nowrap',
          }}
        >
          {status === 'validating' ? (
            <>
              <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
              Verifying...
            </>
          ) : 'Activate'}
        </button>
      </div>

      {errorMessage && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          color: EARTH.terracotta[600], fontSize: '13px',
        }}>
          <AlertCircle style={{ width: '14px', height: '14px', flexShrink: 0 }} />
          <span>{errorMessage}</span>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </form>
  );
}
